import { z } from 'zod'
import { MAX_PLUS_ONES } from './event'
import { landingFeedItemSchema } from './feed'

/** Event RSVP choice; null on rows saved before the status existed (plain interested).
 * `not_going` keeps the row so the choice is remembered, but the user is not
 * attending: not counted, not listed, not notified. */
export const eventRsvpStatusSchema = z.enum(['going', 'maybe', 'not_going'])

export type EventRsvpStatus = z.infer<typeof eventRsvpStatusSchema>

export const interestResponseSchema = z.object({
  interested: z.boolean(),
  /** Event interests only: interested but hidden from friends (list + presence). Absent for listings. */
  hidden: z.boolean().optional(),
  /** Event interests only: the RSVP choice; null = legacy interest without a status. Absent for listings. */
  rsvpStatus: eventRsvpStatusSchema.nullable().optional(),
  /** Event interests only: extra guests coming along with a "Bin dabei". */
  plusOnes: z.number().int().optional(),
})

export type InterestResponse = z.infer<typeof interestResponseSchema>

export const updateInterestHiddenSchema = z.object({
  hidden: z.boolean().optional(),
  status: eventRsvpStatusSchema.optional(),
  /** Extra guests with a "Bin dabei"; clamped to the event's limit, reset by any other answer. */
  plusOnes: z.number().int().min(0).max(MAX_PLUS_ONES).optional(),
})

export type UpdateInterestHidden = z.infer<typeof updateInterestHiddenSchema>

export const myInterestsResponseSchema = z.object({
  data: z.array(landingFeedItemSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
})

export type MyInterestsResponse = z.infer<typeof myInterestsResponseSchema>

export const interestedFriendSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
  rsvpStatus: eventRsvpStatusSchema.nullable().optional(),
})

export const interestedFriendsResponseSchema = z.object({
  /** Total number of users interested (friends + everyone else). */
  totalCount: z.number(),
  /** The caller's accepted friends who are interested, named. */
  friends: z.array(interestedFriendSchema),
})

const publicAttendeeSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
})

/** People who chose to show anyone that they go to or are interested in an event. */
export const eventAttendeesResponseSchema = z.object({
  going: z.array(publicAttendeeSchema),
  interested: z.array(publicAttendeeSchema),
  /**
   * Set on an event on request for someone not accepted: `going` then holds only their own
   * friends who were accepted and show themselves, and `goingCount` counts everyone going.
   */
  locked: z.boolean().optional(),
  goingCount: z.number().int().nonnegative().optional(),
})

export type EventAttendeesResponse = z.infer<typeof eventAttendeesResponseSchema>
export type InterestedFriend = z.infer<typeof interestedFriendSchema>
export type InterestedFriendsResponse = z.infer<typeof interestedFriendsResponseSchema>
